import { useLayoutEffect, useRef } from 'react';
import { SLIDE_IN_READY_SAFETY_TIMEOUT, SLIDE_TRANSFORM_DURATION, SLIDE_EASING } from '../../../constants/animations';

/**
 * Composes the wrapper's transform, combining the slide's vertical translation
 * with each device's static transform (desktop centers via translateX(-50%),
 * fullscreen doesn't center — same composition useFlipAnimation already uses).
 */
const buildTransform = (isDesktop, isFullscreen, translateYVh) => {
  if (isFullscreen) return `translateY(${translateYVh}vh) translateZ(0)`;
  if (isDesktop) return `translateY(${translateYVh}vh) translateX(-50%) translateZ(0)`;
  return `translateY(${translateYVh}vh) translateZ(0)`;
};

// An open Modal applies transform: scale(...) directly on the wrapper
// (ModalRenderer.jsx). Avoid fighting over the property while the Modal owns
// it at trigger time.
const isWrapperScaled = (el) => (el.style.transform || '').includes('scale(');

/**
 * Hook that animates the wrapper's entry (slideInOnMount/slideInReady) and
 * exit (STATUS_CLOSING) via transform: translateY, mirroring
 * useFlipAnimation's architecture (inline styles, not CSS-class-driven) —
 * necessary because other hooks (useFlipAnimation, ModalRenderer) already
 * write directly to style.transform, and inline style always wins over a
 * CSS rule for the same property.
 *
 * @param {React.RefObject} wrapperRef - Ref to the .wrapper element
 * @param {Object} params
 * @param {boolean} params.slideInOnMount - If true, pins the wrapper off screen
 *   on mount (transition:none, so nothing paints in the pinned position).
 * @param {boolean} [params.slideInReady=true] - Gates when the pin above is
 *   released and the slide-in transition actually plays. Deliberately
 *   separate from slideInOnMount: a consumer may know synchronously (at
 *   mount) that it wants the entrance animation, while not yet knowing
 *   whether the content behind it has finished rendering — pinning
 *   immediately and revealing only once slideInReady flips true avoids ever
 *   painting unpinned content the reveal would otherwise race against.
 *   Defaults to `true` (reveal immediately, once painted) to match the
 *   pre-existing single-signal behavior for callers that don't pass it. If
 *   it never flips true, SLIDE_IN_READY_SAFETY_TIMEOUT reveals unanimated
 *   anyway rather than leaving the wrapper pinned off screen forever — this
 *   is not a contract callers need to honor (there's no requirement to ever
 *   pass `true`), just a floor on how long a broken signal can hide content.
 * @param {boolean} params.isClosing - If true (STATUS_CLOSING), animates the exit once
 * @param {boolean} params.isDesktop - Current device, to compose the correct transform
 * @param {boolean} params.isFullscreen - Whether fullscreen (FLIP) is active at the same time
 */
export function useSlideAnimation(
  wrapperRef,
  { slideInOnMount, slideInReady = true, isClosing, isDesktop, isFullscreen },
) {
  const hasPinnedRef = useRef(false);
  const hasRevealedRef = useRef(false);
  // Set once the reveal's cleanupTimeout actually completes (never reset,
  // unlike hasRevealedRef) — see the reveal effect below for why this is
  // separate from hasRevealedRef.
  const hasCompletedRevealRef = useRef(false);
  const hasClosedRef = useRef(false);
  // Shared with the pin effect's safety timeout below: the reveal effect
  // clears it once a real reveal starts, so the safety net never fires after
  // the real thing already happened.
  const safetyTimeoutRef = useRef(null);
  // The double-RAF callback below runs asynchronously (2 frames after this
  // render), so closed-over isDesktop/isFullscreen/isClosing could be stale
  // if any of them change in that window (e.g. a resize flips isDesktop
  // right after mount, or CLOSING starts mid-slide-in). Updated on every
  // render (not in an effect) so it's always current by the time the RAF fires.
  const latestRef = useRef({ isDesktop, isFullscreen, isClosing });
  latestRef.current = { isDesktop, isFullscreen, isClosing };

  // Pin: fires once, on the first mount, when slideInOnMount=true. Separate
  // from the reveal below on purpose — this must happen before ANY paint
  // (synchronously, via useLayoutEffect, transition:none), regardless of
  // whether slideInReady is already true. If this were combined with the
  // reveal (gated on slideInReady too), a consumer whose slideInReady starts
  // false would render unpinned, visible content first and only pin+slide it
  // later once slideInReady flips — sliding in content the user already saw,
  // instead of revealing a clean entrance.
  useLayoutEffect(() => {
    // If CLOSING is already active at mount (e.g. an immediate error forces
    // close before the SDK-S session finishes loading), don't pin at all —
    // closing wins. Without this, the reveal effect below would still be
    // scheduled and could later re-open the wrapper on top of whatever the
    // isClosing effect just set.
    if (!wrapperRef.current || !slideInOnMount || hasPinnedRef.current || isClosing) return undefined;
    if (isWrapperScaled(wrapperRef.current)) return undefined;

    hasPinnedRef.current = true;
    const el = wrapperRef.current;

    el.style.transition = 'none';
    el.style.transform = buildTransform(isDesktop, isFullscreen, 100);
    el.getBoundingClientRect(); // force reflow: commits the off-screen state before the reveal

    // Safety net: if slideInReady never flips true (the destination content
    // errors, a network call it was waiting on never resolves, etc.), the
    // reveal effect below never runs and the wrapper would otherwise stay
    // pinned off-screen — transition:none, nothing scheduled — forever. That
    // state was unreachable before slideInReady existed (pin and reveal were
    // the same effect), and it trades a blank screen for what should at most
    // be a missing entrance animation. Degrades to an instant, unanimated
    // reveal — not the polished slide-in, but not a stuck blank screen either.
    safetyTimeoutRef.current = setTimeout(() => {
      if (hasCompletedRevealRef.current || latestRef.current.isClosing) return;
      hasRevealedRef.current = true;
      hasCompletedRevealRef.current = true;
      el.style.transition = '';
      el.style.transform = '';
    }, SLIDE_IN_READY_SAFETY_TIMEOUT);

    return () => {
      // Resets the one-shot guard so a StrictMode dev-mode replay (mount →
      // cleanup → mount again, synchronously) can re-establish the pin.
      // Mirrors hasClosedRef below, which already resets itself the same way.
      hasPinnedRef.current = false;
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }
    };
  // slideInOnMount is the only real dependency: the hook fires once,
  // controlled by the hasPinnedRef guard, and shouldn't react to
  // isDesktop/isFullscreen changes after the pin has already been applied
  // (isClosing is checked fresh at mount-time above; the reveal effect below
  // is what reads latestRef.current.isClosing for its own async callbacks).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideInOnMount]);

  // Reveal: releases the pin above and plays the slide-in transition, once
  // both slideInOnMount and slideInReady are true. Kept in its own effect
  // (deps=[slideInOnMount, slideInReady]) so a caller can flip slideInReady
  // from false to true independently, any time after mount, without
  // re-running (and re-pinning) the effect above.
  useLayoutEffect(() => {
    if (
      !wrapperRef.current ||
      !slideInOnMount ||
      !slideInReady ||
      !hasPinnedRef.current ||
      hasRevealedRef.current ||
      hasCompletedRevealRef.current ||
      isClosing
    ) {
      return undefined;
    }
    if (isWrapperScaled(wrapperRef.current)) return undefined;

    hasRevealedRef.current = true;
    // The real reveal is proceeding — the pin effect's safety net (in case
    // slideInReady never flipped true) is now moot.
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
    const el = wrapperRef.current;

    let raf1 = null;
    let raf2 = null;
    let cleanupTimeout = null;

    // Double RAF: guarantees the browser already painted the "pinned" state
    // (transition:none + off-screen, set by the effect above) before
    // activating the real transition — a single RAF is not reliable for this
    // (same rationale as useFlipAnimation).
    raf1 = requestAnimationFrame(() => {
      raf1 = null;
      // CLOSING may have started between mount and this frame — don't
      // re-open on top of it.
      if (latestRef.current.isClosing) return;
      raf2 = requestAnimationFrame(() => {
        raf2 = null;
        // Re-check: CLOSING may have started between the two frames.
        if (latestRef.current.isClosing) return;
        const { isDesktop: currentIsDesktop, isFullscreen: currentIsFullscreen } = latestRef.current;
        const revealedTransition = `transform ${SLIDE_TRANSFORM_DURATION / 1000}s ${SLIDE_EASING}`;
        const revealedTransform = buildTransform(currentIsDesktop, currentIsFullscreen, 0);
        el.style.transition = revealedTransition;
        el.style.transform = revealedTransform;

        // Hand the property back to the CSS class once the transition ends,
        // so no inline transform is left "hanging" to confuse the next hook
        // that writes to transform (fullscreen, Modal) — but only if it's
        // still OUR value. slideInReady's flip time (unlike the old
        // single-signal reveal) can land close enough to a later status
        // change (e.g. FULLSCREEN, via useFlipAnimation) that this timeout
        // fires after that hook has already taken over transform/transition;
        // unconditionally clearing then would wipe its translateZ(0) GPU
        // layer and cause a repaint flash. Comparing before clearing means
        // this only ever cleans up after itself.
        cleanupTimeout = setTimeout(() => {
          hasCompletedRevealRef.current = true;
          if (el.style.transform === revealedTransform) el.style.transform = '';
          if (el.style.transition === revealedTransition) el.style.transition = '';
        }, SLIDE_TRANSFORM_DURATION);
      });
    });

    return () => {
      // Resets the one-shot guard for the same StrictMode-replay reason as
      // the pin effect above. hasCompletedRevealRef is deliberately NOT
      // reset here — see its own declaration for why.
      hasRevealedRef.current = false;
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      if (cleanupTimeout) clearTimeout(cleanupTimeout);
    };
  // isClosing deliberately excluded: it's checked once, synchronously, above
  // (using whatever value is in scope when slideInOnMount/slideInReady last
  // changed) and again fresh via latestRef inside the async RAF callbacks —
  // depending on it directly would re-run this effect (resetting
  // hasRevealedRef) on every isClosing flip, letting a later isClosing=false
  // re-arm and replay the reveal on top of an in-progress or completed
  // slide-out.
  //
  // slideInReady, unlike isClosing, IS a real dependency — it's the whole
  // point of this effect existing separately from the pin above. But it's
  // not guaranteed monotonic on the consumer side (e.g. Orca's own
  // slideInReady can drop back to false on a later isLoading flip from an
  // unrelated subsequent data fetch, then true again) — hasCompletedRevealRef
  // is what stops a false->true cycle AFTER a real reveal already completed
  // from re-running this effect and re-taking ownership of transform/
  // transition for another SLIDE_TRANSFORM_DURATION, which would otherwise
  // reopen the same collision window with useFlipAnimation described above,
  // repeatedly, for the rest of the session.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideInOnMount, slideInReady]);

  // Slide-out: terminal in practice (the native app destroys the webview
  // right after), but defensive against a CLOSING aborted by mistake —
  // without cleanup, an isClosing that reverts to false would leave the
  // wrapper stuck off-screen forever. Fires once per cycle (no RAF: there's
  // no pin/measurement here, the browser animates from whatever value is
  // currently computed to the new inline value).
  useLayoutEffect(() => {
    if (!wrapperRef.current || !isClosing || hasClosedRef.current) return undefined;
    if (isWrapperScaled(wrapperRef.current)) return undefined;

    hasClosedRef.current = true;
    const el = wrapperRef.current;

    el.style.transition = `transform ${SLIDE_TRANSFORM_DURATION / 1000}s ${SLIDE_EASING}`;
    el.style.transform = buildTransform(isDesktop, isFullscreen, 100);

    return () => {
      hasClosedRef.current = false;
      el.style.transform = '';
      el.style.transition = '';
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClosing]);
}
